/*
 * AlertsManager — the single owner of the fleet AUDIT LOG + alerts feed, mirroring
 * FleetManager: it SEEDS history via `ListEvents` then LIVE-tails `SubscribeEvents`
 * (reconnect-forever), holds the events newest-first, and emits `change`. Two
 * surfaces, one source:
 *   • the header BELL — it satisfies base-ui's NotificationSource (list /
 *     pendingCount / remove). The X dismisses a notification PERMANENTLY (persisted
 *     to localStorage, so it stays cleared across reloads); opening the bell marks
 *     all read (a lastRead watermark, also persisted).
 *   • the ALERTS VIEW — `events()` returns the full recent feed (the audit history,
 *     NOT dismissible; dismissal is a bell-only concept).
 *
 * The durable, circular `fleet_events` stream on fleet-control is the source of
 * truth; each (re)connect re-seeds the newest window (so a disconnect can't lose
 * history), and the live tail prepends new events (deduped by id).
 */
import { createClient, type Transport } from '@connectrpc/connect';
import { FleetControl, type FleetEvent } from '@vitesse/proto/vitesse/v1/fleet_control_pb.js';
import type { AppNotification, NotificationSource } from '@vitesse/base-ui';

/** Severity rank, ordered for filtering/sorting (info < warning < critical). */
export type AlertSeverity = 'info' | 'warning' | 'critical';

/** A decoded fleet event for the Alerts view + the bell. Plain shape (enums → strings,
 *  ns → ms) so the UI never touches protobuf. */
export interface AlertItem {
  id: string;
  ts: number; // ms since epoch
  gatewayId: string;
  kind: string; // 'online' | 'offline' | 'claimed' | … | 'ids_alert'
  severity: AlertSeverity;
  title: string;
  detail: string;
}

// Numeric enum → string, keyed by the stable proto values (robust to protobuf-es
// member naming — the same approach fleet-client uses for IDS mode/status).
const KIND: Record<number, string> = {
  1: 'online',
  2: 'offline',
  3: 'claimed',
  4: 'unclaimed',
  5: 'revoked',
  6: 'removed',
  7: 'enrolled',
  8: 'version',
  9: 'ids_alert',
  10: 'ids_block',
};
const SEV: Record<number, AlertSeverity> = { 1: 'info', 2: 'warning', 3: 'critical' };

const SEED_LIMIT = 200; // newest-window pulled on each (re)connect
const MAX_ITEMS = 300; // ring kept in memory for the Alerts view
const BELL_MAX = 50; // rows shown in the bell dropdown
const DISMISSED_KEY = 'fleet-alerts-dismissed';
const LASTREAD_KEY = 'fleet-alerts-lastread';

function mapEvent(ev: FleetEvent): AlertItem {
  return {
    id: ev.id,
    ts: Number(ev.timestampNs) / 1e6,
    gatewayId: ev.gatewayId,
    kind: KIND[ev.kind] ?? 'event',
    severity: SEV[ev.severity] ?? 'info',
    title: ev.title,
    detail: ev.detail,
  };
}

function loadSet(key: string): Set<string> {
  try {
    const raw = localStorage.getItem(key);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}
function saveSet(key: string, s: Set<string>): void {
  try {
    localStorage.setItem(key, JSON.stringify([...s]));
  } catch {
    /* private mode / quota — dismissal just won't persist this session */
  }
}
function loadNum(key: string): number {
  try {
    const n = Number(localStorage.getItem(key));
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0; // private mode / storage disabled
  }
}

export class AlertsManager extends EventTarget implements NotificationSource {
  private items: AlertItem[] = []; // newest-first, capped at MAX_ITEMS
  private byId = new Set<string>(); // dedup of the current ring
  private dismissed = loadSet(DISMISSED_KEY); // bell-dismissed ids (persisted)
  private lastReadTs = loadNum(LASTREAD_KEY); // ms watermark (persisted)
  private ac?: AbortController;
  private started = false;
  /** true once the first seed has landed. */
  ready = false;
  /** last stream error ('' when healthy). */
  error = '';

  constructor(private readonly transport: Transport) {
    super();
  }

  start(): void {
    if (this.started) return;
    this.started = true;
    void this.run();
  }

  stop(): void {
    this.started = false;
    this.ac?.abort();
  }

  /** The full recent feed (newest-first) for the Alerts view — the audit history. */
  events(): AlertItem[] {
    return this.items;
  }

  // ── NotificationSource (the header bell) ──
  get list(): AppNotification[] {
    const out: AppNotification[] = [];
    for (const it of this.items) {
      if (this.dismissed.has(it.id)) continue;
      out.push({
        id: it.id,
        kind: it.severity,
        title: it.title,
        body: it.detail || gatewayLabel(it.gatewayId),
        read: it.ts <= this.lastReadTs,
      });
      if (out.length >= BELL_MAX) break;
    }
    return out;
  }

  /* Rows outstanding, not "unread". This counted only rows NEWER than a
     locally-stored lastReadTs, so opening the bell on this browser put the dot out
     while the alert was still listed — and left app and ai still showing it, three
     surfaces disagreeing about one shared fact. Dismissal is the thing that clears
     a row, and dismissal already persists. */
  get pendingCount(): number {
    let n = 0;
    for (const it of this.items) if (!this.dismissed.has(it.id)) n++;
    return n;
  }


  remove(id: string): void {
    this.dismissed.add(id);
    saveSet(DISMISSED_KEY, this.dismissed);
    this.dispatchEvent(new Event('change'));
  }

  // ── stream ownership (mirrors FleetManager) ──
  private async run(): Promise<void> {
    this.ac?.abort();
    const ac = new AbortController();
    this.ac = ac;
    const client = createClient(FleetControl, this.transport);
    while (this.started && !ac.signal.aborted) {
      try {
        // Re-seed the newest window on each (re)connect — the durable stream is the
        // source of truth, so this backfills anything missed while disconnected.
        const seed = await client.listEvents({ limit: SEED_LIMIT }, { signal: ac.signal });
        if (ac.signal.aborted) return;
        this.items = seed.events.map(mapEvent);
        this.byId = new Set(this.items.map((i) => i.id));
        this.ready = true;
        this.error = '';
        this.dispatchEvent(new Event('change'));
        // Live tail — new events as they happen (prepend, deduped).
        const stream = client.subscribeEvents({}, { signal: ac.signal });
        for await (const ev of stream) {
          if (!ev.id) continue; // keep-alive heartbeat (empty id) — flushes headers + proves liveness, carries no event
          this.ingestLive(mapEvent(ev));
          this.dispatchEvent(new Event('change'));
        }
      } catch (err) {
        if (ac.signal.aborted) return; // stop()/refresh owns the next run
        this.error = err instanceof Error ? err.message : String(err);
        this.dispatchEvent(new Event('change'));
        await new Promise((r) => setTimeout(r, 1500)); // backoff, retry forever
      }
    }
  }

  private ingestLive(item: AlertItem): void {
    if (this.byId.has(item.id)) return; // already seeded/seen
    this.byId.add(item.id);
    this.items.unshift(item); // newest-first
    while (this.items.length > MAX_ITEMS) {
      const dropped = this.items.pop();
      if (dropped) this.byId.delete(dropped.id);
    }
  }
}

/** A short gateway label for a notification body when the event carries no detail. */
function gatewayLabel(gw: string): string {
  return gw ? `Relay ${gw.slice(0, 8)}` : 'Fleet';
}
